
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import store from '@/store/index';
import {
    isUndef,
} from '@/lib/utils';
import HorizontalSlideContainer from '@/components/horizontalSlidePage/horizontalSlideContainer.vue';
import HorizontalSlidePage from '@/components/horizontalSlidePage/horizontalSlidePage.vue';

/**
 * @update(names, ids) 数据更新
 * @finish() 点击最后一级的情况下触发事件，可能使用于当点击最后一级的情况下，将关闭地址选择组件
 */
@Component({
    name: 'DistrictPicker',
    components: {
        HorizontalSlideContainer,
        HorizontalSlidePage,
    },
})
export default class DistrictPicker extends Vue {
    /** 默认显示的位置内容 */
    @Prop({ type: Array, default() { return []; } }) public defaultDistrictIds!: number[];
    /** 最大的level，默认为3，只允许显示 <= maxLevel 的内容 */
    @Prop({ type: Number, default: 3 }) public maxLevel!: number;
    /** 是否使用额外数据 */
    @Prop({ type: Boolean, default: false }) public useExtra!: boolean;
    @Prop() public level2!: boolean;

    /** 当前显示的内容 */
    public current: number = 0;
    /** 所有的districtId内容，其中不允许存在null */
    public districtIds: DistrictId[] = [];
    /** 第二级地区列表 */
    public level2List: District[] = [];
    /** 第三级地区列表 */
    public level3List: District[] = [];

    /** 第一级选择内容 */
    public get level1List() {
        let list = store.state.district._provinceList;
        if (this.useExtra) {
            list = store.state.district.extraProvinceList.concat(list);
        }
        return list;
    }
    /** 所有的list的map */
    public get listMap() {
        return store.state.district._listMap;
    }
    /** 所有的district的map */
    public get districtMap() {
        return store.state.district._districtMap;
    }
    /**
     * 如果没有的情况下，返回null
     *
     * @return {Number|null} 最后一级districtId，当没有districtIds时候返回null
     */
    public get lastDistrictId() {
        const len = this.districtIds.length;
        if (len === 0) {
            return null;
        }
        return this.districtIds[len - 1];
    }
    /**
     * 指示下一列是否存在
     *
     * 1. 当前列数>=3的情况 -> false
     * 2. isUndef(lastDistrictId) -> true
     * 3. district的level达到标准的情况下 -> false
     * 4. !list == null && !(list.length === 0)
     */
    public get moreColumnVisible() {
        // 如果列数已经到达了3
        if (this.districtIds.length >= 3) {
            return false;
        }
        // 如果最后一个districtId不存在情况下，表明当前没有任何级别被选中
        const lastDistrictId = this.lastDistrictId;
        if (isUndef(lastDistrictId)) {
            return true;
        }
        // 如果lastDistrictId已经到达了最大的level，那么将不显示
        const district = this.districtMap[lastDistrictId as number];
        if (this.level2 && this.maxLevel === 2 && district.level === 1 && district.is_city) {
            return true;
        }
        if (district && district.level >= this.maxLevel) {
            return false;
        }
        let list = this.listMap[lastDistrictId as number];
        if (isUndef(list)) {
            return false;
        }
        // 如果最后一个districtId对应的list是一个空数组的情况下，表明没有下一级了
        list = list.filter((district) => district.level <= this.maxLevel);
        return !isUndef(list) && !(list.length === 0);
    }
    /**
     * 最大的column数量
     *
     * @return {Number} 最大的column存在数量
     */
    public get maxColumn() {
        return this.districtIds.length + (this.moreColumnVisible ? 1 : 0);
    }
    /**
     * 所需要显示的面包屑导航
     *
     * - 当没有的情况下，将会默认显示一列内容
     */
    public get districtNames() {
        if (this.districtIds.length > 0) {
            const result = this.districtIds.map((districtId) => {
                const district = this.districtMap[districtId];
                return district ? district.name : null;
            });
            if (this.moreColumnVisible) {
                result.push('请选择');
            }
            return result;
        } else {
            return [
                '请选择',
            ];
        }
    }

    /**
     * 当districtIds发生变化的时候，需要尝试获取子级行政区的内容
     */
    @Watch('districtIds')
    /**
     * 检查districtIds的内容，保证districtIds中的id的子级行政区是存在的
     */
    public onDistrictIdsChange(ids: number[]) {
        const maxLevel = this.maxLevel;
        // 当所有的数据完成之后，在确定需要移动到哪里
        // 显示在不同的list中的内容需要保证level符合要求
        ids.forEach((id, index) => {
            if (index === 0) {
                const topDistrict = this.districtMap[id];
                this.level2List = this.listMap[id].filter((district) => {
                    if (this.level2 && maxLevel > 1 && topDistrict && topDistrict.is_city) {
                        return true;
                    }
                    return district && district.level <= maxLevel;
                });
            } else if (index === 1) {
                this.level3List = this.listMap[id].filter((district) => {
                    return district && district.level <= maxLevel;
                });
            }
        });
    }

    /**
     * 当address被点击
     *
     * @param {} id 行政区id
     * @param {} column 列数，从0开始
     */
    public async onClickAddressItem(id: number, column: number) {
        // 删除被点击column开始之后的所有id，保留column之前的所有的id
        const ids = this.districtIds.slice(0, column);
        // 更新ids
        ids.push(id);

        this.$store.commit('startRequest');
        // 获取更新所有的数据
        await Promise.all(ids.map((id) => this.$store.dispatch('getChildDistricts', { id })));
        this.$store.commit('endRequest');
        this.districtIds = ids;
        // 判断是否还有下一列的存在情况下，进入到column的下一列中
        if (column + 2 <= this.maxColumn) {
            this.current = column + 1;
        }
        // 如果存在"请选择"的情况下，将删除"请选择"
        const names = this.moreColumnVisible
            ? this.districtNames.slice(0, this.districtNames.length - 1)
            : this.districtNames;
        this.$emit('update', names, ids);
        // 当没有更多需要显示的情况下，将触发finish事件
        if (!this.moreColumnVisible) {
            this.$emit('finish');
        }
    }
    /**
     * 点击面包屑的时候
     *
     * @param {} index 导航的位置
     */
    public onClickAddressCrumb(index: number) {
        this.current = index;
    }

    // 生命周期
    public async created() {
        // 确保provinceList全部获取
        const ids = (this.defaultDistrictIds || []).filter((id) => {
            return !isUndef(id);
        });
        // 只有在需要的时候才进行请求
        if (ids.length > 0) {
            this.$store.dispatch('getProvinceList');
        }
        // 获取子级district
        await Promise.all(ids.map((id) => {
            return this.$store.dispatch('getChildDistricts', { id });
        }));
        ids.forEach((id, index) => {
            Vue.set(this.districtIds, index, id);
        });
        this.current = ids.length > 0 ? ids.length - 1 : 0;
    }
}
